import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import {
  clearShiftProgramDetails,
  getShiftProgramDetails,
  runShiftProgramNow,
} from 'features/operations/shiftProgramsSlice'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { tokens } from 'styling/tokens'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IShiftProgramRunResponse } from 'types/api/operationsShiftProgramsApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import {
  formatWeeklyDays,
  getRunStatusColor,
  getRunStatusLabel,
  getShiftProgramStatusLabel,
} from '../shiftProgramsData'

const runColumns: IHonestTableColumn<IShiftProgramRunResponse>[] = [
  {
    id: 'shift',
    header: 'Shift',
    sticky: true,
    minWidth: '12rem',
    cell: (item) => (
      <Cell>
        <BodyText weight='600'>{item.shift?.name ?? '-'}</BodyText>
        <OtherText grey>{item.shiftDate ? formatDateForDisplay(item.shiftDate) : '-'}</OtherText>
      </Cell>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    minWidth: '8rem',
    cell: (item) => (
      <Cell>
        <BodyText>{getRunStatusLabel(item)}</BodyText>
      </Cell>
    ),
  },
  {
    id: 'generated',
    header: 'Generated',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.generatedCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'skipped',
    header: 'Skipped',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.skippedCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'errors',
    header: 'Errors',
    align: 'right',
    minWidth: '6rem',
    cell: (item) => (
      <RightCell>
        <BodyText>{item.errorCount}</BodyText>
      </RightCell>
    ),
  },
  {
    id: 'startedAt',
    header: 'Started',
    align: 'right',
    minWidth: '10rem',
    cell: (item) => (
      <RightCell>
        <OtherText grey>{formatDateForDisplay(item.startedAt, true)}</OtherText>
      </RightCell>
    ),
  },
  {
    id: 'source',
    header: 'Source',
    align: 'right',
    minWidth: '8rem',
    cell: (item) => (
      <RightCell>
        <OtherText grey>{item.source}</OtherText>
      </RightCell>
    ),
  },
]

export const SingleShiftProgram = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { shiftProgramDetails } = useSelector((state: RootState) => state.shiftPrograms)
  const details = shiftProgramDetails

  useEffect(() => {
    dispatch(getShiftProgramDetails(id))
    return () => {
      dispatch(clearShiftProgramDetails())
    }
  }, [id])

  const handleRun = async () => {
    await dispatch(runShiftProgramNow(id))
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex center gap='1rem' width='100%' between >
          <Flex gap='1rem' center>

            <H1WithBack arrow title={details?.name ?? 'Shift Program'} />
            {details && (
              <StatusPill status={details.status !== 2 ? 'success' : 'accent'}>{getShiftProgramStatusLabel(details.status)}</StatusPill>
            )}
          </Flex>

          <Flex gap='1rem' >
            <Button variant='primary' onClick={() => navigate('edit')}>Edit</Button>
            {details?.status === 2 && (
              <Button variant='primary' onClick={handleRun} >
                Run
              </Button>
            )}
          </Flex>
        </Flex>

        {details && (
          <>
            <Flex column gap='0.5rem' style={{ margin: '2rem 0' }}>
              <Flex gap='1rem'>
                <MetricCard value={details.shiftCount} label='Shifts' accentColor={tokens.accent.base} />
                <MetricCard value={details.runCount} label='Total runs' accentColor={tokens.accent.base} />
                <MetricCard
                  value={details.status === 2 ? '—' : details.nextRunAt ? formatDateForDisplay(details.nextRunAt, true) : '-'}
                  label='Next run'
                  accentColor={details.status === 2 ? tokens.text.muted : tokens.accent.base}
                />
                <MetricCard
                  value={details.lastRun ? getRunStatusLabel(details.lastRun) : 'N / A'}
                  label='Last run'
                  accentColor={details.lastRun ? getRunStatusColor(details.lastRun) : tokens.accent.base}
                />
                <MetricCard value={details.bindings?.length ?? 0} label='Bindings' accentColor={tokens.accent.base} />
              </Flex>
              {details.status === 2 && (
                <OtherText grey>Paused — runs resume when the program is resumed</OtherText>
              )}
            </Flex>

            <Flex column gap='1rem' width='100%'>
              <Container>
                <ContainerHeader>
                  <TitleText>Identity</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>NAME</BodyText>
                      <BodyText>{details.name}</BodyText>
                    </Flex>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>NOTES</BodyText>
                      <BodyText>{details.notes || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Scope</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>SITE</BodyText>
                      <BodyText>{details.site?.name ?? '-'}</BodyText>
                    </Flex>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>TEAMS</BodyText>
                      <BodyText>{details.teams?.map((team) => team.name).join(', ') || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Recurrence</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <Flex column gap='1rem'>
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>FREQUENCY</BodyText>
                      <BodyText>{details.recurrenceLabel}</BodyText>
                    </Flex>
                    {details.recurrenceType === 3 && (
                      <Flex gap='5rem' alignStart>
                        <BodyText grey>DAYS</BodyText>
                        <BodyText>{formatWeeklyDays(details.weeklyDays)}</BodyText>
                      </Flex>
                    )}
                    {details.recurrenceType === 4 && (
                      <Flex gap='5rem' alignStart>
                        <BodyText grey>INTERVAL</BodyText>
                        <BodyText>{details.customIntervalDays ? `${details.customIntervalDays} days` : '-'}</BodyText>
                      </Flex>
                    )}
                    <Flex gap='5rem' alignStart>
                      <BodyText grey>SHIFTS</BodyText>
                      <BodyText>{details.shifts?.map((shift) => shift.name).join(', ') || '-'}</BodyText>
                    </Flex>
                  </Flex>
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Task Bindings</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  {details.bindings?.length ? (
                    <Flex column gap='1rem'>
                      {details.bindings.map((binding) => (
                        <Flex key={binding.id} column gap='0.5rem'>
                          <Flex gap='5rem' alignStart>
                            <BodyText grey>TASK</BodyText>
                            <BodyText>{binding.taskTemplate?.name ?? '-'}</BodyText>
                          </Flex>
                          <Flex gap='5rem' alignStart>
                            <BodyText grey>LOCATIONS</BodyText>
                            <BodyText>
                              {binding.locations?.map((location) => location.name).join(', ') || '-'}
                            </BodyText>
                          </Flex>

                        </Flex>
                      ))}
                    </Flex>
                  ) : (
                    <BodyText grey>No bindings configured</BodyText>
                  )}
                </ContainerContent>
              </Container>

              <Container>
                <ContainerHeader>
                  <TitleText>Run history</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  {details.runs?.length ? (
                    <HonestTable columns={runColumns} data={details.runs} keyField='id' />
                  ) : (
                    <BodyText grey>No runs yet</BodyText>
                  )}
                </ContainerContent>
              </Container>

            </Flex>
          </>
        )}
      </PageWrapper>
    </>
  )
}
